<template>
  <div class="card card-commission-chart mb-0">
    <div class="card-header text-center bg-transparent">
      <h2 class="font-weight-bold mb-0">{{ position }}</h2>
      <span>Commissions</span>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="loading-spinner text-center">
        <div class="spinner-border" role="status" />
        <small>Loading charts..</small>
      </div>
      <div v-else class="row commissions-chart">
        <div class="col-12 col-md-6">
          <bar-chart
            type="Commission Rules"
            :chart-data="commissionRulesChart.data"
            :label-colors="commissionRulesChart.labelColors"
          />
        </div>
        <div class="col-12 col-md-6">
          <bar-chart
            type="Commission Qty Enhancers"
            :chart-data="commissionQtyChart.data"
            :label-colors="commissionQtyChart.labelColors"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '../../../components/charts/BarChart.vue';
import { paymentSettings } from "../../../services/api/commissions";
import { formatNumber } from "../../../utils/helpers";

import '../../../../sass/commissions/chart-payment-settings.scss';

export default {
  name: 'CommissionPaymentSettingsChart',
  components: {
    BarChart
  },
  props: {
    userId: {
      type: Number,
      default: 0,
    },
    month: {
      type: Number,
      default: 0
    },
    year: {
      type: Number,
      default: 0
    },
    position: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      isLoading: false,
      commissionRulesChart: {
        data: {
          labels: [],
          datasets: []
        },
        labelColors: []
      },
      commissionQtyChart: {
        data: {
          labels: [],
          datasets: []
        },
        labelColors: []
      }
    };
  },
  mounted() {
    this.fetchActiveCommissionSettings();
  },
  watch: {
    userId: 'fetchActiveCommissionSettings',
    month: 'fetchActiveCommissionSettings',
    year: 'fetchActiveCommissionSettings'
  },
  methods: {
    async fetchActiveCommissionSettings() {
      try {
        this.isLoading = true;
        const response = await paymentSettings.active(this.userId, this.year, this.month);

        if (response.status === 200) {
          this.updateChartData(response.data.rules, response.data.qty_enhancers, response.data.total_students);
        }
      } catch (error) {
        console.error('Error fetching active commission settings:', error);

        new Noty({
          type: "error",
          text: 'Failed to fetch active commission payment settings. Please try again later.',
        }).show();
      } finally {
        this.isLoading = false;
      }
    },
    updateChartData(rules, qty_enhancers, total_students) {
      // Filter qty_enhancers to exclude items with percentage above 200
      // Only show the first item with percentage 200
      const filteredQtyEnhancers = qty_enhancers.filter(item => parseFloat(item.percentage) <= 200);
      const maxPercentage = filteredQtyEnhancers.findIndex(item => parseFloat(item.percentage) === 200);

      if (maxPercentage !== -1) {
        filteredQtyEnhancers.splice(maxPercentage + 1);
      }

      this.commissionRulesChart.data = {
        labels: rules.map(item => `$${formatNumber(item.min_payment)}-${formatNumber(item.max_payment)}`),
        datasets: [
          {
            backgroundColor: '#2395B7',
            data: rules.map(item => parseFloat(item.commission)),
            barPercentage: 0.95,
            categoryPercentage: 0.9
          }
        ]
      };

      this.commissionRulesChart.labelColors = rules.map(item => ({
        backgroundColor: '#7070F2'
      }));

      this.commissionQtyChart.data = {
        labels: filteredQtyEnhancers.map(item => item.min_students),
        datasets: [
          {
            backgroundColor: filteredQtyEnhancers.map((item) => this.getQtyBackgroundColor(item, total_students)),
            data: filteredQtyEnhancers.map(item => parseFloat(item.percentage)),
            barPercentage: 0.95,
            categoryPercentage: 0.9
          }
        ]
      };

      this.commissionQtyChart.labelColors = filteredQtyEnhancers.map((item) => ({
        backgroundColor: this.getQtyBackgroundColor(item, total_students)
      }));
    },
    getQtyBackgroundColor(item, total_students) {
      if (item.percentage === '100.00' || item.percentage === '200.00') {
        return '#04BCA6';
      }
      if (total_students >= item.min_students && total_students <= item.max_students) {
        return '#7070F2';
      }

      return '#2395B7';
    }
  }
};
</script>
